import { DataType, UIFieldType } from '@lio/rule-engine';
import { FormFieldType, TFormField } from '@models/forms/formFields.type';
import { setDateToMidnight } from '@utils/date';
import { getUndefinedIfEmpty } from '@utils/getUndefinedIfEmpty';

/**
 * Generic helper function to get the value of a field based on its type.
 * @param formValues The form values object.
 * @param fields The fields object containing field configurations.
 * @returns The processed value for the field.
 */
export function formatFormValues<T extends Record<string, any>>(
  formValues: T,
  fields: {
    [fieldName: string]: TFormField;
  },
  options?: {
    visibleOnly?: boolean;
  },
): Record<string, any> {
  const result: any = {};

  Object.entries(formValues).forEach(([fieldName, value]) => {
    let skip = false;

    // if the option to only include visible fields is set anything not shown to undefined
    if (options?.visibleOnly) {
      const field = fields[fieldName];
      if (!field?.isVisible) {
        skip = false;
      } else if (typeof field?.isVisible === 'function') {
        skip = !field.isVisible({
          data: { watchedField: formValues[field['watchedField'] as keyof typeof formValues] },
        });
      } else {
        skip = !field.isVisible;
      }
    }

    if (skip) {
      result[fieldName] = undefined;
    } else {
      const fieldConfig = fields[fieldName]?.fieldConfig;
      switch (fields[fieldName]?.fieldType) {
        case FormFieldType.DatePickerField: {
          result[fieldName] = value ? setDateToMidnight(value as Date) : undefined;
          break;
        }
        case FormFieldType.FilteredSelectField: {
          result[fieldName] = getUndefinedIfEmpty(value?.value);
          break;
        }
        case FormFieldType.MultiSelectField: {
          result[fieldName] = Array.isArray(value) ? value.map((v: any) => v.value) : [];
          break;
        }
        case FormFieldType.RadioGroupField: {
          if (value === 'true') {
            result[fieldName] = true;
          } else if (value === 'false') {
            result[fieldName] = false;
          } else {
            result[fieldName] = value;
          }

          break;
        }
        default:
          if (fields[fieldName]?.fieldConfig?.textField?.inputType === 'number') {
            if (value && fieldConfig?.uiFieldType === UIFieldType.Percentage) {
              value = value / 100;
            }

            result[fieldName] = value ?? undefined;
            break;
          }

          if (value !== undefined && value !== null) {
            if (fieldConfig?.dataType === DataType.Date) {
              value = setDateToMidnight(value);
            }
          }

          if (fieldConfig?.dataType === DataType.Boolean && value !== undefined) {
            value = value === true || value === 'true' ? true : false;

            result[fieldName] = value;
          } else {
            result[fieldName] = getUndefinedIfEmpty(value);
          }
          break;
      }
    }
  });

  return result;
}
